import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { showToast } from '../../utils/helpers';

const ViewVerificationDetailsModal = ({ show, onClose, userId, requestedName, status, data }) => {
  const [verificationDetails, setVerificationDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);

  const [certificateLoadError, setCertificateLoadError] = useState(false);
  useEffect(() => {
    if (show && userId && requestedName) {
      fetchVerificationDetails();
    } else {
      setVerificationDetails(null);
    }
  }, [show, userId, requestedName]);

  const fetchVerificationDetails = async () => {
    if (!userId || !requestedName) {
      showToast('Missing user information', 'error');
      return;
    }

    setIsLoading(true);
    try {
      setVerificationDetails(data);
    } catch (error) {
      console.error('Error fetching rejection details:', error);
      showToast('Failed to load rejection details', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileType = (url) => {
    if (!url) return null;
    const extension = url.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) return 'image';
    return 'unknown';
  };

  const handleViewCertificate = () => {
    if (!verificationDetails?.certificate_url) {
      showToast('No certificate available', 'warning');
      return;
    }
    setShowCertificateModal(true);
  };

const CertificateModal = () => {
  if (!showCertificateModal || !verificationDetails?.certificate_url) return null;



  // Get the appropriate URLs from the backend data
  const getCertificateData = () => {
    const viewUrl = verificationDetails.certificate_url; // For viewing (PDFs converted to images)
    const downloadUrl = verificationDetails.certificate_download_url || viewUrl; // For download (original file)
    const resourceType = verificationDetails.resource_type; // 'pdf' or 'image' from backend
    const isPdf = resourceType === 'pdf' || verificationDetails.certificate_url?.includes('.pdf');
    
    return {
      viewUrl,
      downloadUrl,
      resourceType,
      isPdf
    };
  };

  const { viewUrl, downloadUrl, resourceType, isPdf } = getCertificateData();

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/70 z-[60]">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold ">Business Certificate</h3>
            {isPdf && (
              <p className="text-sm text-gray-500 mt-1">
                PDF displayed as image for preview
              </p>
            )}
          </div>
          <button
            onClick={() => {
              setShowCertificateModal(false);
              setCertificateLoadError(false);
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Certificate Content */}
        <div className="flex-1 p-6 overflow-auto">
          {isLoading && !certificateLoadError && (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading certificate...</p>
              </div>
            </div>
          )}

          {certificateLoadError ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h4 className="text-lg font-medium text-gray-700 mb-2">Failed to Load Certificate</h4>
              <p className="text-gray-600 mb-4">The certificate could not be loaded.</p>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Certificate
              </a>
            </div>
          ) : (
            <div className="text-center">
              {/* Info banner for PDF files */}
              {isPdf && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-center space-x-2 text-blue-800">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm">
                      This is a preview of your PDF certificate. Download the original for full document.
                    </span>
                  </div>
                </div>
              )}
              
              <img
                src={viewUrl}
                alt="Business Certificate"
                className={`max-w-full mx-auto border border-gray-200 rounded-lg shadow-sm ${
                  isPdf ? 'max-h-96 md:max-h-[500px]' : 'max-h-96 md:max-h-[600px]'
                }`}
                onLoad={() => setIsLoading(false)}
                onError={() => {
                  setIsLoading(false);
                  setCertificateLoadError(true);
                  console.error('Failed to load certificate:', viewUrl);
                }}
                style={{ display: isLoading ? 'none' : 'block' }}
              />
            </div>
          )}
        </div>

        {/* Footer with Actions */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {isPdf ? 'PDF Certificate' : 'Business Certificate'}
            {resourceType && (
              <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                {resourceType.toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            {/* Download Button - Always show download for original file */}
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              onClick={() => {
                // Close modal after a short delay to allow download to start
                setTimeout(() => setShowCertificateModal(false), 500);
              }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {isPdf ? 'Download PDF' : 'Download'}
            </a>
            
            {/* View Original in New Tab */}
            <a
              href={viewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Open in New Tab
            </a>

            <button
              onClick={() => {
                setShowCertificateModal(false);
                setCertificateLoadError(false);
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
  if (!show) return null;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
        <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold ">Verification Details</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading verification details...</p>
              </div>
            </div>
          ) : verificationDetails ? (
            <div className="space-y-6">
              {/* Request Summary */}
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-medium  mb-3">Request Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">From:</span>
                    <p className="font-medium text-gray-900">{verificationDetails.former_company_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">To:</span>
                    <p className="font-medium text-gray-900">{verificationDetails.requested_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      verificationDetails.status === 'rejected' 
                        ? 'bg-red-100 text-red-800'
                        : verificationDetails.status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {verificationDetails.status?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Reviewed On:</span>
                    <p className="font-medium text-gray-900">
                      {formatDate(verificationDetails.reviewed_at)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Certificate Section - Show for all statuses if certificate exists */}
              {verificationDetails.certificate_url && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-3">Business Certificate</h4>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-700 text-sm">
                        {getFileType(verificationDetails.certificate_url) === 'pdf' 
                          ? 'PDF Document' 
                          : 'Image Certificate'
                        }
                      </p>
                    </div>
                    <button
                      onClick={handleViewCertificate}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Certificate
                    </button>
                  </div>
                </div>
              )}

              {/* Rejection Reason */}
              <div>
                <h4 className="text-lg font-medium  mb-3">
                  {verificationDetails.status === 'rejected' ? 'Reason for Rejection' : 'Review Notes'}
                </h4>
                <div className={`rounded-lg p-4 ${
                  verificationDetails.status === 'rejected' 
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-blue-50 border border-blue-200'
                }`}>
                  <p className={`whitespace-pre-wrap ${
                    verificationDetails.status === 'rejected' ? 'text-red-800' : 'text-blue-800'
                  }`}>
                    {verificationDetails.rejection_reason || verificationDetails.notes || 'No specific notes provided.'}
                  </p>
                </div>
              </div>

              {/* Reviewer Information */}
              {verificationDetails.reviewed_by && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Reviewed By</h4>
                  <p className="text-blue-700">{verificationDetails.reviewed_by}</p>
                </div>
              )}

              {/* Call to Action for Rejected Status */}
              {verificationDetails.status === 'rejected' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-green-800 mb-1">Think this was a mistake?</h5>
                      <p className="text-green-700 text-sm mb-3">
                        You can submit a reapplication with additional information or corrections to address the concerns mentioned above.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="text-lg font-medium text-gray-700 mb-2">No Details Found</h4>
              <p className="text-gray-600 mb-4">We couldn't find any verification details for this request.</p>
              <button
                onClick={fetchVerificationDetails}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Certificate Modal */}
      <CertificateModal />
    </>
  );
};

export default ViewVerificationDetailsModal;